import { useCallback, useRef } from 'react';
/**
 * Константы для определения оси ограничения
 */
const MIN_MOVEMENT_THRESHOLD = 3; // Минимальное смещение для определения оси (в пикселях)
/**
 * Хук для ограничения перемещения элемента строго по горизонтали или вертикали при зажатой Shift.
 *
 * Поддерживает:
 * - Зажатие Shift в начале перетаскивания
 * - Зажатие Shift во время перетаскивания
 * - Автоматическое определение оси ограничения на основе направления движения
 * - Работу с несколькими элементами одновременно
 */
export const useShiftDragConstraint = () => {
    // Храним состояние перетаскивания для каждого элемента (начальная позиция и ось ограничения)
    const dragStatesRef = useRef(new Map());
    /**
     * Сохраняет начальную позицию элемента при начале перетаскивания
     */
    const saveStartPosition = useCallback((target) => {
        const elementId = target.id();
        dragStatesRef.current.set(elementId, {
            startPosition: {
                x: target.x(),
                y: target.y(),
            },
            axis: null,
        });
    }, []);
    /**
     * Определяет ось ограничения на основе смещения от начальной позиции
     */
    const determineAxis = useCallback((deltaX, deltaY) => {
        // Если смещение слишком мало, ось еще не определена
        if (deltaX < MIN_MOVEMENT_THRESHOLD && deltaY < MIN_MOVEMENT_THRESHOLD) {
            return null;
        }
        // Выбираем ось с большим смещением
        return deltaX > deltaY ? 'x' : 'y';
    }, []);
    /**
     * Применяет ограничение перемещения к элементу
     */
    const applyConstraint = useCallback((target, dragState) => {
        const currentX = target.x();
        const currentY = target.y();
        const { startPosition, axis } = dragState;
        if (axis === 'x') {
            // Ограничение по горизонтали: фиксируем Y
            target.position({ x: currentX, y: startPosition.y });
        }
        else if (axis === 'y') {
            // Ограничение по вертикали: фиксируем X
            target.position({ x: startPosition.x, y: currentY });
        }
    }, []);
    /**
     * Обработчик начала перетаскивания
     * Всегда сохраняет начальную позицию элемента, независимо от состояния Shift
     */
    const handleDragStart = useCallback((e, target) => {
        // Сохраняем начальную позицию элемента при начале перетаскивания
        saveStartPosition(target);
    }, [saveStartPosition]);
    /**
     * Обработчик движения при перетаскивании
     */
    const handleDragMove = useCallback((e, target) => {
        var _a, _b;
        const elementId = target.id();
        const isShiftPressed = (_b = (_a = e.evt) === null || _a === void 0 ? void 0 : _a.shiftKey) !== null && _b !== void 0 ? _b : false;
        // Получаем состояние перетаскивания элемента
        const dragState = dragStatesRef.current.get(elementId);
        if (!dragState) {
            // Если состояние не найдено, создаем его с текущей позицией
            // (на случай, если dragStart не был вызван)
            saveStartPosition(target);
            return;
        }
        // Если Shift не зажата, просто сбрасываем ось ограничения, но сохраняем начальную позицию
        if (!isShiftPressed) {
            dragState.axis = null;
            return;
        }
        // Вычисляем смещение от начальной позиции (точки начала перетаскивания)
        const currentX = target.x();
        const currentY = target.y();
        const deltaX = Math.abs(currentX - dragState.startPosition.x);
        const deltaY = Math.abs(currentY - dragState.startPosition.y);
        // Определяем ось ограничения, если еще не определена
        if (dragState.axis === null) {
            const newAxis = determineAxis(deltaX, deltaY);
            if (newAxis === null) {
                return; // Ждем большего движения для определения оси
            }
            dragState.axis = newAxis;
        }
        // Применяем ограничение
        applyConstraint(target, dragState);
    }, [saveStartPosition, determineAxis, applyConstraint]);
    /**
     * Обработчик окончания перетаскивания
     */
    const handleDragEnd = useCallback((target) => {
        const elementId = target.id();
        dragStatesRef.current.delete(elementId);
    }, []);
    return {
        handleDragStart,
        handleDragMove,
        handleDragEnd,
    };
};
